// ceilingGrid.js — read the ceiling grid drawn inside a room on a reflected ceiling plan.
//
// Acoustic tile ceilings are drawn as a regular grid (2'×4', 2'×2', 600×1200, 600×600): two families of
// parallel lines at a fixed spacing that run across the room. A room with no such grid is a drywall
// (gypsum board) ceiling — or an open ceiling the estimator decides about.
//
//   ceilingGrid(sheet, ring, ftPerUnit) → { kind: "2x4" | "2x2" | "grid" | "none", spacing: [a, b] | [a] | [], label, guessed }
//   (guessed: only one direction was a clear grid — a small room; the caller may follow the sheet's usual grid)
//
// Units: sheet units (ring and segments), ftPerUnit = feet per sheet unit.

const SEG_CURVE = 4;

function pointIn([x, y], ring) {
  let ins = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i], [xj, yj] = ring[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) ins = !ins;
  }
  return ins;
}
function distToRing([x, y], ring) {
  let best = Infinity;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [ax, ay] = ring[j], [bx, by] = ring[i];
    const dx = bx - ax, dy = by - ay, L2 = dx * dx + dy * dy || 1e-12;
    const t = Math.max(0, Math.min(1, ((x - ax) * dx + (y - ay) * dy) / L2));
    best = Math.min(best, Math.hypot(x - ax - t * dx, y - ay - t * dy));
  }
  return best;
}
const near = (v, target, tol = 0.12) => Math.abs(v - target) <= target * tol;

export function ceilingGrid(sheet, ring, ftPerUnit) {
  const none = { kind: "none", spacing: [], label: "no grid" };
  if (!ring || ring.length < 3 || !(ftPerUnit > 0)) return none;
  const U = (ft) => ft / ftPerUnit;
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const [x, y] of ring) { x0 = Math.min(x0, x); y0 = Math.min(y0, y); x1 = Math.max(x1, x); y1 = Math.max(y1, y); }
  const S = sheet.segs, F = sheet.segF, n = S.length >> 2;
  const segs = [];
  const edge = U(0.4);   // lines on the room outline are its walls, not grid
  for (let i = 0; i < n; i++) {
    if (F[i] & SEG_CURVE) continue;
    const ax = S[i * 4], ay = S[i * 4 + 1], bx = S[i * 4 + 2], by = S[i * 4 + 3];
    const mx = (ax + bx) / 2, my = (ay + by) / 2;
    if (mx < x0 || mx > x1 || my < y0 || my > y1) continue;
    const len = Math.hypot(bx - ax, by - ay);
    if (len < U(1.2)) continue;
    if (!pointIn([mx, my], ring) || distToRing([mx, my], ring) < edge) continue;
    let a = Math.atan2(by - ay, bx - ax); if (a < 0) a += Math.PI; if (a >= Math.PI) a -= Math.PI;
    segs.push({ ax, ay, bx, by, mx, my, len, a });
  }
  if (segs.length < 3) return none;

  // the two grid directions: the strongest orientation and the one square to it
  const bins = new Float64Array(180);
  for (const s of segs) bins[Math.round((s.a * 180) / Math.PI) % 180] += s.len;
  let b0 = 0; for (let i = 1; i < 180; i++) if (bins[i] > bins[b0]) b0 = i;
  const dirs = [b0, (b0 + 90) % 180].map((deg) => (deg * Math.PI) / 180);

  const fam = dirs.map((ang) => {
    const ux = Math.cos(ang), uy = Math.sin(ang), nx = -uy, ny = ux;
    const ls = segs.filter((s) => { let d = Math.abs(s.a - ang) % Math.PI; d = Math.min(d, Math.PI - d); return d < 0.035; });
    // the room's extent along this direction (how long a grid line spanning it would be)
    let t0 = Infinity, t1 = -Infinity, o0 = Infinity, o1 = -Infinity;
    for (const [x, y] of ring) { const t = ux * x + uy * y, o = nx * x + ny * y; t0 = Math.min(t0, t); t1 = Math.max(t1, t); o0 = Math.min(o0, o); o1 = Math.max(o1, o); }
    // chopped pieces of one grid line share an offset — total their length
    const offs = ls.map((s) => ({ o: nx * s.mx + ny * s.my, len: s.len })).sort((p, q) => p.o - q.o);
    const lines = [];
    for (const s of offs) {
      const last = lines[lines.length - 1];
      if (last && s.o - last.o <= U(0.15)) { last.len += s.len; last.o = (last.o * last.k + s.o) / (last.k + 1); last.k++; }
      else lines.push({ o: s.o, len: s.len, k: 1 });
    }
    // grid lines run most of the way across the room (fixtures and diffusers don't)
    const span = t1 - t0;
    const long = lines.filter((l) => l.len >= Math.min(span * 0.45, U(6)));
    // (one long line: remember the bays it makes against the walls — used when the other way is a clear grid)
    const edgeBays = long.map((l) => [(l.o - o0) * ftPerUnit, (o1 - l.o) * ftPerUnit]).flat();
    if (long.length < 2) return { P: null, edgeBays };
    const gaps = [];
    for (let i = 1; i < long.length; i++) gaps.push((long[i].o - long[i - 1].o) * ftPerUnit);
    // modal spacing between 1 ft and 6 ft
    let best = null;
    for (const g of gaps) {
      if (g < 0.9 || g > 6.2) continue;
      const cnt = gaps.filter((h) => near(h, g, 0.08)).length;
      if (!best || cnt > best.cnt) best = { g, cnt };
    }
    if (!best) return { P: null, edgeBays: [...edgeBays, ...gaps] };
    const vals = gaps.filter((h) => near(h, best.g, 0.08));
    const P = vals.reduce((a, b) => a + b, 0) / vals.length;
    const width = (o1 - o0) * ftPerUnit;
    // a real grid repeats: at least two equal bays, or one in a room only a couple of tiles wide
    if (best.cnt < 2 && !(best.cnt >= 1 && width <= P * 3.5)) return { P: null, edgeBays: [...edgeBays, ...gaps] };
    return { P: Math.round(P * 4) / 4, cnt: best.cnt };
  });

  let guessed = false;
  // small rooms (toilets, closets) hold only a line or two the other way: take the biggest whole bay
  if (fam.filter((f) => f.P).length === 1) {
    const other = fam.find((f) => !f.P);
    const bays = (other.edgeBays || []).filter((g) => g >= 1.6 && g <= 6.2);
    const full = bays.filter((g) => near(g, 4, 0.1) || near(g, 2, 0.1));
    if (full.length) { other.P = Math.round(Math.max(...full) * 4) / 4; guessed = true; }
  }
  const ps = fam.map((f) => f.P).filter(Boolean).sort((a, b) => a - b);
  if (!ps.length) return none;
  const ft = (v) => `${Number.isInteger(v) ? v : v.toFixed(1)}'`;
  // lines one way only are bulkheads, linear fixtures or slots in a drywall ceiling, not a tile grid
  if (ps.length === 1) return none;
  const [a, b] = ps;
  if (near(a, 2) && near(b, 4)) return { kind: "2x4", spacing: ps, label: "2'×4' grid", guessed };
  if (near(a, 2) && near(b, 2)) return { kind: "2x2", spacing: ps, label: "2'×2' grid", guessed };
  // an unusual spacing only counts as a grid when it clearly repeats both ways (3+ equal bays each)
  if (fam.every((f) => (f.cnt || 0) >= 3)) return { kind: "grid", spacing: ps, label: `${ft(a)}×${ft(b)} grid` };
  return none;
}

// what grid an assembly / ceiling type describes, from its name, tag and build-up text
export function gridOfText(text) {
  const s = String(text || "").toLowerCase().replace(/[’′]/g, "'").replace(/\s+/g, " ");
  if (/\b2\s*'?\s*(?:-0"?)?\s*[x×]\s*4\b|\b600\s*[x×]\s*1200\b|\b24\s*"?\s*[x×]\s*48\b/.test(s)) return "2x4";
  if (/\b2\s*'?\s*(?:-0"?)?\s*[x×]\s*2\b|\b600\s*[x×]\s*600\b|\b24\s*"?\s*[x×]\s*24\b/.test(s)) return "2x2";
  if (/\b(gyp|gypsum|gwb|drywall|plasterboard|board ceiling|bulkhead|type x)\b/.test(s)) return "none";
  if (/\b(act|acoustic|t-?bar|lay-?in|suspended|tile)\b/.test(s)) return "grid";
  return null;
}
